import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, Download, ExternalLink, Copy, Check, Wallet } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export const ReceiptViewer = ({ bookingId, onClose }) => {
  const [receipt, setReceipt] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [cancelMode, setCancelMode] = useState(null); // 'resell' or 'refund'
  const [refundWallet, setRefundWallet] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReceipt();
    fetchBooking();
  }, [bookingId]);

  const fetchReceipt = async () => {
    try {
      setLoading(true);
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/receipts/booking/${bookingId}`);
      const data = await response.json();
      
      if (data.success) {
        setReceipt(data.receipt);
      } else {
        toast.error('Receipt not available yet');
      }
    } catch (error) {
      console.error('Error fetching receipt:', error);
      toast.error('Failed to load receipt');
    } finally {
      setLoading(false);
    }
  };

  const fetchBooking = async () => {
    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/bookings/${bookingId}`);
      const data = await response.json();
      if (data.booking) {
        setBooking(data.booking);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
    }
  };

  const handleRequestRefund = async () => {
    if (!refundWallet || !refundWallet.match(/^0x[a-fA-F0-9]{40}$/)) {
      toast.error('Please enter a valid wallet address for refund');
      return;
    }

    try {
      setSubmitting(true);
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/cancellations/request-refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: bookingId,
          guest_wallet: refundWallet,
          reason: refundReason
        })
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Refund requested: ${result.message}`);
        setCancelMode(null);
        fetchBooking(); // Refresh booking data
      } else {
        toast.error(result.message || 'Failed to request refund');
      }
    } catch (error) {
      console.error('Refund request error:', error);
      toast.error('Failed to submit refund request');
    } finally {
      setSubmitting(false);
    }
  };

  const isBeforeCheckIn = () => {
    if (!booking?.check_in) return false;
    const checkInDate = new Date(booking.check_in);
    return new Date() < checkInDate;
  };

  const hasNFT = booking?.nft_token_id !== undefined && booking?.nft_token_id !== null;
  const canCancel = booking?.payment_method === 'crypto' && booking?.payment_status === 'confirmed' && isBeforeCheckIn() && !booking?.refund_status;

  const copyHash = () => {
    navigator.clipboard.writeText(receipt.receipt_hash);
    setCopied(true);
    toast.success('Receipt hash copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadReceipt = () => {
    const receiptText = `
getbitsy.ai - Booking Receipt
==============================

Receipt ID: ${receipt.receipt_id}
Generated: ${new Date(receipt.generated_at).toLocaleString()}

Hotel Information
-----------------
Name: ${receipt.hotel_name}
Location: ${receipt.hotel_location}
Room: ${receipt.room_name}

Booking Details
---------------
Guest: ${receipt.guest_name}
Check-in: ${receipt.check_in}
Check-out: ${receipt.check_out}
Guests: ${receipt.guests}

Payment Summary
---------------
Total Paid: $${receipt.total_paid}
Payment Method: ${receipt.payment_method}
${receipt.crypto_details ? `
Crypto Payment
--------------
Chain: ${receipt.crypto_details.chain}
Asset: ${receipt.crypto_details.asset}
Amount: ${receipt.crypto_details.amount}
TX Hash: ${receipt.crypto_details.tx_hash}
Hotel Wallet: ${receipt.crypto_details.hotel_wallet}
Network: ${receipt.crypto_details.network}
` : ''}

Verification
------------
Receipt Hash: ${receipt.receipt_hash}
Verification URL: ${receipt.verification_url}

This receipt is cryptographically secured and can be verified using the hash above.
    `.trim();

    const blob = new Blob([receiptText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${receipt.receipt_id}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Receipt downloaded!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="receipt-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!receipt) {
    return (
      <Alert data-testid="receipt-not-found">
        <AlertDescription>
          Receipt not available. It will be generated after payment confirmation.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6" data-testid="receipt-viewer">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-6 w-6 text-green-600" />
            <h2 className="text-2xl font-bold tracking-tight">Booking Receipt</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Cryptographically verified and tamper-proof
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={downloadReceipt}
            data-testid="download-receipt-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-receipt-btn">
              Close
            </Button>
          )}
        </div>
      </div>

      {/* Verification Badge */}
      <Alert className="border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800" data-testid="receipt-verified-alert">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-900 dark:text-green-100">
          <strong>Verified Receipt</strong> - This booking has been cryptographically verified and recorded.
        </AlertDescription>
      </Alert>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Receipt Details */}
        <div className="md:col-span-2 space-y-6">
          {/* NFT Receipt Card (if minted) */}
          {hasNFT && (
            <Card className="border-2 border-[hsl(var(--crypto))] bg-gradient-to-br from-[hsl(var(--crypto))]/5 to-transparent" data-testid="receipt-nft-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[hsl(var(--crypto))]/20 flex items-center justify-center">
                      <Shield className="w-4 h-4 text-[hsl(var(--crypto))]" />
                    </div>
                    <div>
                      <CardTitle className="text-base">NFT Booking Receipt</CardTitle>
                      <CardDescription className="text-xs">On-chain proof of booking</CardDescription>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[hsl(var(--crypto))] border-[hsl(var(--crypto))]">
                    {isBeforeCheckIn() ? 'Transferable' : 'Locked'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-card/60 border border-border rounded-xl p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Token ID</span>
                    <span className="mono font-semibold">#{booking.nft_token_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Chain</span>
                    <Badge variant="outline" className="text-xs uppercase">{booking.nft_chain}</Badge>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-muted-foreground">Contract</span>
                    <span className="mono text-[10px] text-right break-all max-w-[180px]">
                      {booking.nft_contract_address?.slice(0, 8)}...{booking.nft_contract_address?.slice(-6)}
                    </span>
                  </div>
                  {booking.nft_royalty_percent && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Hotel Royalty</span>
                      <span className="font-semibold">{booking.nft_royalty_percent}%</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1 text-xs"
                    onClick={() => {
                      const chain = booking.nft_chain;
                      const chainSlug = chain === 'ethereum' ? 'sepolia' : chain === 'base' ? 'base-sepolia' : 'polygon-amoy';
                      window.open(`https://testnets.opensea.io/assets/${chainSlug}/${booking.nft_contract_address}/${booking.nft_token_id}`, '_blank');
                    }}
                    data-testid="receipt-opensea-link"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" /> OpenSea
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1 text-xs"
                    onClick={() => {
                      const explorers = {
                        ethereum: 'sepolia.etherscan.io',
                        base: 'sepolia.basescan.org',
                        polygon: 'amoy.polygonscan.com'
                      };
                      window.open(`https://${explorers[booking.nft_chain]}/token/${booking.nft_contract_address}?a=${booking.nft_token_id}`, '_blank');
                    }}
                    data-testid="receipt-explorer-link"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" /> Explorer
                  </Button>
                </div>

                {isBeforeCheckIn() && (
                  <Alert className="bg-[hsl(var(--info))]/10 border-[hsl(var(--info))]/30">
                    <AlertDescription className="text-xs">
                      <strong>Resellable:</strong> This NFT can be transferred until check-in. After {new Date(booking.check_in).toLocaleDateString()}, it becomes a collectible.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Hybrid Cancellation Options (crypto bookings only) */}
          {canCancel && (
            <Card className="border-orange-200 bg-orange-50/30" data-testid="receipt-cancellation-card">
              <CardHeader>
                <CardTitle className="text-base">Cancellation Options</CardTitle>
                <CardDescription className="text-xs">
                  Choose how you'd like to handle this booking
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {!cancelMode && (
                  <div className="grid grid-cols-2 gap-3">
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex-col items-start text-left border-2 hover:border-primary hover:bg-primary/5"
                      onClick={() => setCancelMode('resell')}
                      data-testid="cancel-option-resell"
                      disabled={!hasNFT}
                    >
                      <span className="font-semibold text-sm mb-1">Resell NFT</span>
                      <span className="text-xs text-muted-foreground">List on OpenSea for market price</span>
                      {!hasNFT && <Badge variant="outline" className="text-[9px] mt-2">NFT Required</Badge>}
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex-col items-start text-left border-2 hover:border-primary hover:bg-primary/5"
                      onClick={() => setCancelMode('refund')}
                      data-testid="cancel-option-refund"
                    >
                      <span className="font-semibold text-sm mb-1">Request Refund</span>
                      <span className="text-xs text-muted-foreground">Get {booking.refund_pct}% back from hotel</span>
                    </Button>
                  </div>
                )}

                {/* Resell Flow */}
                {cancelMode === 'resell' && hasNFT && (
                  <div className="space-y-3 p-3 bg-card/60 border border-border rounded-xl">
                    <p className="text-sm">To resell your booking NFT:</p>
                    <ol className="text-xs text-muted-foreground space-y-1 ml-4 list-decimal">
                      <li>Go to OpenSea (link above)</li>
                      <li>Connect the wallet that owns the NFT</li>
                      <li>Set your listing price</li>
                      <li>Buyer receives the booking, hotel gets {booking.nft_royalty_percent}% royalty</li>
                    </ol>
                    <Button 
                      variant="default" 
                      size="sm" 
                      className="w-full"
                      onClick={() => {
                        const chain = booking.nft_chain;
                        const chainSlug = chain === 'ethereum' ? 'sepolia' : chain === 'base' ? 'base-sepolia' : 'polygon-amoy';
                        window.open(`https://testnets.opensea.io/assets/${chainSlug}/${booking.nft_contract_address}/${booking.nft_token_id}`, '_blank');
                      }}
                      data-testid="resell-opensea-button"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" /> List on OpenSea
                    </Button>
                    <Button variant="ghost" size="sm" className="w-full" onClick={() => setCancelMode(null)}>
                      Back to Options
                    </Button>
                  </div>
                )}

                {/* Refund Flow */}
                {cancelMode === 'refund' && (
                  <div className="space-y-3 p-3 bg-card/60 border border-border rounded-xl">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1.5 block">Your Wallet (for refund)</label>
                      <input 
                        type="text"
                        placeholder="0x..."
                        value={refundWallet}
                        onChange={(e) => setRefundWallet(e.target.value)}
                        className="w-full px-3 py-2 text-xs mono border border-border rounded-lg bg-background"
                        data-testid="refund-wallet-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1.5 block">Reason (optional)</label>
                      <textarea 
                        placeholder="Why are you canceling?"
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        className="w-full px-3 py-2 text-xs border border-border rounded-lg bg-background resize-none"
                        rows="2"
                        data-testid="refund-reason-input"
                      />
                    </div>
                    <Alert className="bg-orange-50 border-orange-200">
                      <AlertDescription className="text-xs">
                        <strong>Refund Policy:</strong> You'll receive {booking.refund_pct}% refund based on cancellation timing. The hotel will send crypto to your wallet.
                      </AlertDescription>
                    </Alert>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      className="w-full"
                      onClick={handleRequestRefund}
                      disabled={submitting || !refundWallet}
                      data-testid="submit-refund-button"
                    >
                      {submitting ? 'Submitting...' : `Request ${booking.refund_pct}% Refund`}
                    </Button>
                    <Button variant="ghost" size="sm" className="w-full" onClick={() => setCancelMode(null)}>
                      Back to Options
                    </Button>
                  </div>
                )}

                {booking?.refund_status && booking.refund_status !== 'none' && (
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription className="text-xs">
                      <strong>Refund Status:</strong> {booking.refund_status.replace('_', ' ').toUpperCase()}
                      {booking.refund_expected_amount_usd && (
                        <span className="block mt-1">Expected: ${booking.refund_expected_amount_usd}</span>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Receipt Info Card */}
          <Card data-testid="receipt-info-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{receipt.receipt_id}</CardTitle>
                  <CardDescription>
                    Generated {new Date(receipt.generated_at).toLocaleDateString()} at{' '}
                    {new Date(receipt.generated_at).toLocaleTimeString()}
                  </CardDescription>
                </div>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Confirmed
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Hotel & Room */}
              <div>
                <h4 className="font-semibold mb-2">Hotel & Accommodation</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Hotel:</span>
                    <span className="font-medium">{receipt.hotel_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Location:</span>
                    <span>{receipt.hotel_location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Room:</span>
                    <span>{receipt.room_name}</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Booking Details */}
              <div>
                <h4 className="font-semibold mb-2">Booking Details</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Guest:</span>
                    <span className="font-medium">{receipt.guest_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Check-in:</span>
                    <span>{new Date(receipt.check_in).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Check-out:</span>
                    <span>{new Date(receipt.check_out).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Guests:</span>
                    <span>{receipt.guests}</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Payment Info */}
              <div>
                <h4 className="font-semibold mb-2">Payment Summary</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Paid:</span>
                    <span className="font-bold text-lg">${receipt.total_paid}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Method:</span>
                    <span className="capitalize">{receipt.payment_method.replace('_', ' ')}</span>
                  </div>
                </div>
              </div>

              {/* Crypto Details */}
              {receipt.crypto_details && (
                <>
                  <Separator />
                  <div data-testid="crypto-payment-details">
                    <h4 className="font-semibold mb-2">Cryptocurrency Payment</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Chain:</span>
                        <Badge variant="secondary" className="capitalize">{receipt.crypto_details.chain}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Amount:</span>
                        <span className="font-mono">{receipt.crypto_details.amount} {receipt.crypto_details.asset}</span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-muted-foreground">Transaction:</span>
                        <a 
                          href={`https://basescan.org/tx/${receipt.crypto_details.tx_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-mono text-xs break-all"
                        >
                          {receipt.crypto_details.tx_hash}
                          <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        </a>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Network:</span>
                        <Badge variant="outline" className="capitalize">{receipt.crypto_details.network}</Badge>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* QR Code & Hash Verification */}
        <div className="space-y-4">
          {/* QR Code Card */}
          <Card data-testid="receipt-qr-card">
            <CardHeader>
              <CardTitle className="text-base">Verification QR</CardTitle>
              <CardDescription className="text-xs">
                Scan to verify receipt
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <div className="bg-white p-4 rounded-lg">
                <QRCodeCanvas 
                  value={receipt.verification_url} 
                  size={160}
                  level="H"
                  includeMargin={true}
                />
              </div>
            </CardContent>
          </Card>

          {/* Hash Verification Card */}
          <Card data-testid="receipt-hash-card">
            <CardHeader>
              <CardTitle className="text-base">Receipt Hash</CardTitle>
              <CardDescription className="text-xs">
                Cryptographic proof of authenticity
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 font-mono text-xs break-all">
                {receipt.receipt_hash}
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={copyHash}
                data-testid="copy-hash-btn"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Hash
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                This hash proves the receipt's authenticity and can be independently verified
              </p>
            </CardContent>
          </Card>

          {/* Verification Link */}
          <Card data-testid="verification-link-card">
            <CardContent className="pt-6">
              <Button 
                variant="default" 
                className="w-full"
                onClick={() => window.open(receipt.verification_url, '_blank')}
                data-testid="verify-receipt-btn"
              >
                <Shield className="h-4 w-4 mr-2" />
                Verify Receipt
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Technical Details */}
      <Card className="border-dashed" data-testid="technical-details-card">
        <CardHeader>
          <CardTitle className="text-sm">Technical Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span className="text-muted-foreground">Receipt Version:</span>
              <span className="ml-2 font-mono">1.0</span>
            </div>
            <div>
              <span className="text-muted-foreground">Hash Algorithm:</span>
              <span className="ml-2 font-mono">SHA-256</span>
            </div>
            <div>
              <span className="text-muted-foreground">Booking ID:</span>
              <span className="ml-2 font-mono text-xs">{receipt.booking_id}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Status:</span>
              {hasNFT ? (
                <Badge variant="outline" className="ml-2 text-xs bg-[hsl(var(--crypto))]/10 text-[hsl(var(--crypto))] border-[hsl(var(--crypto))]">
                  On-chain Verified
                </Badge>
              ) : (
                <Badge variant="outline" className="ml-2 text-xs">
                  Cryptographic Hash
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReceiptViewer;
